import { useCallback, useRef, useState } from 'react';
import { Button } from 'antd';
import { LuCloudUpload, LuFolder, LuX } from 'react-icons/lu';
import { topFolderOf } from './utils/selection';
import styles from './DicomDropzone.module.css';

interface DicomDropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

const isAcceptable = (file: File): boolean => {
  const lower = file.name.toLowerCase();
  return (
    lower.endsWith('.dcm') ||
    lower.endsWith('.dicom') ||
    lower.endsWith('.zip') ||
    // Accept files with no extension — common for DICOM data
    !lower.includes('.')
  );
};

interface SelectionEntry {
  key: string;
  name: string;
  /** True for a picked folder, shown as one entry. */
  folder: boolean;
  files: File[];
}

/** Loose files one per entry, each picked folder as a single entry. */
function groupSelection(files: File[]): SelectionEntry[] {
  const entries: SelectionEntry[] = [];
  const folders = new Map<string, SelectionEntry>();
  files.forEach((file) => {
    const folder = topFolderOf(file);
    if (folder === null) {
      entries.push({
        key: `${file.name}-${file.size}-${file.lastModified}`,
        name: file.name,
        folder: false,
        files: [file],
      });
      return;
    }
    let entry = folders.get(folder);
    if (!entry) {
      entry = {
        key: `folder:${folder}`,
        name: folder,
        folder: true,
        files: [],
      };
      folders.set(folder, entry);
      entries.push(entry);
    }
    entry.files.push(file);
  });
  return entries;
}

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${(bytes / 1024).toFixed(1)} KB`;

export default function DicomDropzone({
  files,
  onChange,
  disabled,
}: DicomDropzoneProps) {
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (incoming: File[]) => {
      const filtered = incoming.filter(isAcceptable);
      if (filtered.length === 0) return;
      onChange(filtered);
    },
    [onChange]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;
      const dropped = Array.from(e.dataTransfer.files);
      handleFiles(dropped);
    },
    [disabled, handleFiles]
  );

  const onSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    handleFiles(selected);
    // Reset both inputs via their refs so re-selecting the same file fires
    // change again. Going through the ref avoids mutating the event object.
    if (filesInputRef.current) filesInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  // A picked folder is one entry, not one per file inside it.
  const entries = groupSelection(files);

  const remove = (entry: SelectionEntry) => {
    onChange(files.filter((file) => !entry.files.includes(file)));
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.zone} ${isDragging ? styles.dragging : ''} ${
          disabled ? styles.disabled : ''
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <LuCloudUpload size={32} className={styles.icon} aria-hidden />
        <p className={styles.primaryLine}>
          Drop DICOM files or folders here, or browse from your computer.
        </p>
        <p className={styles.secondaryLine}>
          Studies will be grouped automatically.
        </p>
        <div className={styles.buttonRow}>
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => filesInputRef.current?.click()}
          >
            Select files
          </Button>
          <Button
            disabled={disabled}
            onClick={() => folderInputRef.current?.click()}
          >
            Browse folder
          </Button>
        </div>
        <p className={styles.hint}>
          Supports individual .dcm files, multi-file series, and ZIP-archived
          DICOM folders
        </p>
      </div>

      {/*
        Native <input type="file"> controls render a "Choose Files" button by
        default. Render them outside the visible flow with both display:none
        and visually-hidden positioning so the browser never paints them.
      */}
      <input
        ref={filesInputRef}
        type="file"
        multiple
        accept=".dcm,.dicom,.zip,application/dicom,application/zip"
        className={styles.hiddenInput}
        style={{ display: 'none' }}
        onChange={onSelectFiles}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        // @ts-expect-error -- non-standard folder picker; supported in
        // Chromium and WebKit; modern Firefox honors it via the same prop.
        webkitdirectory=""
        className={styles.hiddenInput}
        style={{ display: 'none' }}
        onChange={onSelectFiles}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />

      {entries.length > 0 && (
        <ul className={styles.fileList}>
          {entries.map((entry) => (
            <li key={entry.key} className={styles.fileItem}>
              {entry.folder && (
                <LuFolder size={14} className={styles.folderIcon} aria-hidden />
              )}
              <span className={styles.fileName}>{entry.name}</span>
              <span className={styles.fileSize}>
                {entry.folder &&
                  `${entry.files.length} ${entry.files.length === 1 ? 'file' : 'files'} · `}
                {formatSize(entry.files.reduce((sum, f) => sum + f.size, 0))}
              </span>
              <button
                type="button"
                aria-label={`Remove ${entry.name}`}
                className={styles.removeButton}
                onClick={() => remove(entry)}
                disabled={disabled}
              >
                <LuX size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
